'use client';

import classNames from 'classnames';
import {default as Image} from '@/components/global/images/resized-cdn-image';
import { useParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';

import { getReviews, StrapiReview } from '@/apis/strapi';
import { doTranslation } from '@/app/i18n/client';
import ReadMoreReview from '@/components/schedule-results/read-more-review';
import { CarrierImage } from '@/utils/carriers';
import CarrierLogo from '@/components/global/carrier-logo/carrier-logo';
import { formatLongDateString } from '@/utils/schedules';

import styles from '@/components/schedule-results/reviews.module.scss';

type Props = {
  aggregateRating: number | null;
  carrierImages: { [key: string]: CarrierImage };
  ratingCount: number | null | undefined;
  maxRating: number | null | undefined;
  minRating: number | null | undefined;
  reviewCount: number | null | undefined;
  cityPairId: string;
  pageCount: number;
  pageSize: number;
  reviews: StrapiReview[];
  totalReviews: number;
};

const Reviews = (props: Props): React.JSX.Element => {
  const { lng }: any = useParams();
  const [reviews, setReviews] = useState(props.reviews);
  const [currentPage, setCurrentPage] = useState(1);

  const isFirstLoad = useRef(true);

  const fetchReviews = async (page: number) => {
    try {
      const newReviews = await getReviews(props.cityPairId, page, props.pageSize);
      setReviews(newReviews);
      setCurrentPage(page);
      if (isFirstLoad.current) {
        isFirstLoad.current = false;
      }
    } catch (error) { }
  };

  const handlePageChange = async (newPage: number) => {
    if (newPage > 0 && newPage <= props.pageCount) {
      await fetchReviews(newPage);
    }
  };

  const getPageNumbers = (currentPage: number, totalPages: number) => {
    const maxVisibleButtons = 3;
    const pages = [];
    const start = Math.max(1, currentPage - Math.floor(maxVisibleButtons / 2));
    const end = Math.min(totalPages, start + maxVisibleButtons - 1);
    for (let number = start; number <= end; number++) {
      pages.push(number);
    }
    return pages;
  };

  const { t } = doTranslation(lng);

  useEffect(() => {
    try {
      reviews.forEach((review) => {
        document.getElementById(review.id.toString())?.setAttribute('tabindex', '0');
      });
      if (!isFirstLoad.current) {
        document.getElementById(reviews[0].id.toString())?.focus();
      }
    } catch (error) { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviews]);

  if (!reviews || !reviews.length) {
    return <></>;
  }

  return (
    <div aria-labelledby="customer-reviews" className={styles.reviews} role="region">
      <div className={styles.title}>
        <div className={styles['frame-mobile']}>
          <h2 className={styles.text} id="customer-reviews">
            {t('Customer Reviews')}
          </h2>
          {props.aggregateRating && (
            <div className={styles['rating-mobile']}>
              <Image
                alt={t('star')}
                height={23}
                src="/assets/images/global/schedule-reviews/star.svg"
                width={23}
                aria-hidden="true"
                loading='lazy'
              />
              <span className={styles.number}>
                {props.aggregateRating}
                <span className="sr-only"> {t('Stars')}</span>
              </span>
            </div>
          )}
        </div>
        <div className={styles.frame}>
          {props.aggregateRating && (
            <div className={styles.rating}>
              <Image
                alt={t('star')}
                height={23}
                src="/assets/images/global/schedule-reviews/star.svg"
                width={23}
                aria-hidden="true"
                loading='lazy'
              />
              <span className={styles.number}>
                {props.aggregateRating}
                <span className="sr-only"> {t('Stars')}</span>
              </span>{' '}
              <span className={styles.text}>
                ({t('{{ totalReviews }} reviews', { totalReviews: props.totalReviews })})
              </span>
            </div>
          )}
        </div>
      </div>
      <div className={styles.list}>
        {reviews.length &&
          reviews.map((review: StrapiReview, index: number) => {
            const carrierId = review.attributes?.carrier_name?.data?.attributes?.carrier_id || '';
            const carrierName = review.attributes?.carrier_name?.data?.attributes?.carrier_name || '';
            const { carrierImages } = props;
            return (
              <div aria-label={t('review')} className={styles.box} id={review.id.toString()} key={review.id}>
                <div className={styles.reviewer}>
                  <span className={styles.name}>{review.attributes.reviewer}</span>
                  <span className={styles.date}>
                    {review.attributes.publishedAt ? formatLongDateString(review.attributes.publishedAt) : ''}
                  </span>
                  {carrierImages && carrierImages[carrierId] && (
                    <CarrierLogo carrierImage={carrierImages[carrierId]} />
                  )}
                </div>
                <div className={styles.review}>
                  <div className={styles.rating}>
                    {Array.from({ length: 5 }, (_, index) => index + 1).map((number) => {
                      if (review.attributes.review_score && number <= review.attributes.review_score) {
                        return (
                          <Image
                            alt={t('star')}
                            height={23}
                            key={number}
                            src="/assets/images/global/schedule-reviews/star.svg"
                            width={23}
                            aria-hidden="true"
                            loading='lazy'
                          />
                        );
                      }
                      if (review.attributes.review_score && review.attributes.review_score >= number - 0.5) {
                        return (
                          <Image
                            alt={t('half star')}
                            height={23}
                            key={number}
                            src="/assets/images/global/schedule-reviews/star-half.svg"
                            width={23}
                            aria-hidden="true"
                            loading='lazy'
                          />
                        );
                      }
                      return (
                        <Image
                          alt={t('empty star')}
                          height={23}
                          key={number}
                          src="/assets/images/global/schedule-reviews/star-gray.svg"
                          width={23}
                          aria-hidden="true"
                          loading='lazy'
                        />
                      );
                    })}
                  </div>
                  <div className={styles.text}>
                    <ReadMoreReview t={t}>{review.attributes.review_text}</ReadMoreReview>
                  </div>
                </div>
              </div>
            );
          })}
        {props.totalReviews > props.pageSize && (
          <div className={styles.link}>
            <span className={styles.text}>
              {t('Showing')} <span className={styles.number}>{reviews.length}</span> {t('out of')}{' '}
              <span className={styles.number}>{props.totalReviews}</span>
            </span>
            <div className={styles.pagination}>
              <button
                className={classNames({
                  [styles.button]: true,
                  [styles.disabled]: currentPage === 1
                })}
                onClick={async () => await handlePageChange(currentPage - 1)}
              >
                <span
                  className={classNames({
                    [styles.text]: true,
                    [styles.disabled]: currentPage === 1
                  })}
                >
                  {t('Previous')}
                </span>
              </button>
              {getPageNumbers(currentPage, props.pageCount).map((pageNumber) => (
                <button
                  className={classNames({
                    [styles.page]: true,
                    [styles.active]: reviews.length && currentPage === pageNumber
                  })}
                  key={pageNumber}
                  onClick={async () => await handlePageChange(pageNumber)}
                >
                  <span className={styles.text}>{pageNumber}</span>
                </button>
              ))}
              <button
                className={classNames({
                  [styles.button]: true,
                  [styles.disabled]: currentPage === props.pageCount
                })}
                onClick={async () => await handlePageChange(currentPage + 1)}
              >
                <span
                  className={classNames({
                    [styles.text]: true,
                    [styles.disabled]: currentPage === props.pageCount
                  })}
                >
                  {t('Next')}
                </span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reviews;
